import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_INVITATION_DETAILS_REFERENCE_DATA_TYPES, OrganizationInvitationDetailsReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getOrganizationInvitationDetailsReduxRequest } from "@redux-requests/organization-invitation-details";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getAllowedAxnAdminUserPropertyBundles } from "@utils/validation/organization/organization-users/organization-users-util";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { OrganizationInvitationDetailsComponent } from "./organization-invitation-details-component/organization-invitation-details-component";
import { getSessionState } from "@redux/selectors/root-selectors";
import { getOrganizationInvitationDetailsDataState } from "@redux/selectors/organization-invitation-details-selectors";

export const OrganizationInvitationDetailsContainer = () => {
    const { axnAdminUserInvitationId } = useParams<{ axnAdminUserInvitationId: string }>();

    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { 
        isLoadingAxnAdminUserInvitation,
        axnAdminUserInvitation
    } = useSelector(getOrganizationInvitationDetailsDataState)
    const referenceData = useReferenceDataSelector<OrganizationInvitationDetailsReferenceData>(ORGANIZATION_INVITATION_DETAILS_REFERENCE_DATA_TYPES);

    const [isDisabled, setIsDisabled] = useState(true);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAdminUserInvitation, setShouldLoadAxnAdminUserInvitation] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.organizationInvitationDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_INVITATION_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.ORGANIZATION_INVITATION_DETAILS
            );
        }
    }, [referenceData.isLoading.organizationInvitationDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAdminUserPropertyBundles &&
            referenceData.axnCompanies &&
            referenceData.axnRelyingParties
        );
    }, [
        referenceData.axnAdminUserPropertyBundles,
        referenceData.axnCompanies,
        referenceData.axnRelyingParties
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAdminUserInvitation && shouldLoadAxnAdminUserInvitation) {
            setShouldLoadAxnAdminUserInvitation(false);
            getOrganizationInvitationDetailsReduxRequest(dispatch)(
                axnAdminUserInvitationId,
                referenceData.axnAdminUserPropertyBundles,
                referenceData.axnCompanies,
                referenceData.axnRelyingParties
            );
        }
    }, [isReferenceDataPresent, isLoadingAxnAdminUserInvitation, shouldLoadAxnAdminUserInvitation]);

    const allowedAxnAdminPropertyBundles = useMemo(() => {
        return getAllowedAxnAdminUserPropertyBundles(
            session,
            referenceData.axnRelyingParties,
            referenceData.axnAdminUserPropertyBundles
        );
    }, [session, referenceData.axnRelyingParties, referenceData.axnAdminUserPropertyBundles]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAdminUserInvitation || referenceData.isLoading.organizationInvitationDetails;
    }, [isLoadingAxnAdminUserInvitation, referenceData.isLoading.organizationInvitationDetails]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAdminUserInvitation &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadAxnAdminUserInvitation
        );
    }, [
        isLoading,
        axnAdminUserInvitation,
        isReferenceDataPresent,
        shouldLoadReferenceData,
        shouldLoadAxnAdminUserInvitation
    ]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.organizationInvitationDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <OrganizationInvitationDetailsComponent
                        session={session}
                        axnAdminUserInvitation={axnAdminUserInvitation}
                        axnAdminUserPropertyBundles={allowedAxnAdminPropertyBundles}
                        axnCompanies={referenceData.axnCompanies}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        isDisabled={isDisabled}
                        setIsDisabled={setIsDisabled}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};